<template>
  <!-- <label class="ux-field-title" v-if="showLabel">{{label}} <span class="ux-required-marker" v-if="required">*</span></label> -->
  <!-- <div class="ux-field-description" v-if="showDescription">{{description}}</div> -->
  <div v-if="multiValue">
    <flex-row class="ux-text-row">
      <flex-cell>
        <compile
          :template="renderedTemplate"
          :parent="stableParent"
          v-memo="[renderedTemplate]"
        />
      </flex-cell>
      <flex-cell shrink vcenter>
        <ux-button tag="a" icon v-if="canRemoveValue" @click="remove(entry)">
          <ux-icon icon="fa-times" />
        </ux-button>
      </flex-cell>
    </flex-row>
    <ux-button v-if="canAddValue" @click="add()">{{ addLabel }}</ux-button>
  </div>
  <template v-else>
    <compile
      :template="renderedTemplate"
      :parent="stableParent"
      v-memo="[renderedTemplate]"
    />
  </template>
</template>
<script>
import { markRaw } from "vue";
import DOMPurify from "dompurify";
import InputMixin from "./input-mixin";
import Expressions from "../expressions";

// A non-reactive, empty parent for vue3-runtime-template. Without this,
// it reads $data/$props/$options off the real $parent at render time
// and re-renders (rebuilding a new component definition each time, which
// causes a full unmount/remount) whenever any parent state changes —
// e.g. on hover, focus, touch tracking, etc. The authored HTML template
// is sandboxed: interpolation is handled here, and custom components
// like <ux-image> are globally registered so they resolve without help
// from $parent.
const STABLE_PARENT = markRaw({ $data: {}, $props: {}, $options: {} });

function escapeHtml(str) {
  const s = String(str);
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const ALLOWED_CUSTOM_TAGS = ["ux-image"];
const ALLOWED_CUSTOM_ATTRS = ["item", "width", "height", "title"];

// DOMPurify returns a stub without addHook/sanitize when imported in an
// environment without a DOM (SSR). Lazy-register the hook on first use so
// importing this module doesn't blow up on the server.
let hookRegistered = false;
function ensureHook() {
  if (hookRegistered || typeof DOMPurify.addHook !== "function") return;
  // Strip any Vue-binding attributes so authored HTML can't execute
  // arbitrary expressions or events against the host component.
  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    const name = data.attrName || "";
    if (
      name.startsWith("v-") ||
      name.startsWith("@") ||
      name.startsWith(":") ||
      name.startsWith("on")
    ) {
      data.keepAttr = false;
    }
  });
  hookRegistered = true;
}

function sanitize(html) {
  if (typeof DOMPurify.sanitize !== "function") return "";
  ensureHook();
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ALLOWED_CUSTOM_TAGS,
    ADD_ATTR: ALLOWED_CUSTOM_ATTRS,
  });
}

export default {
  props: {
    modelValue: {
      type: [String, Array],
    },
    context: {
      type: Object,
    },
  },
  mixins: [InputMixin],
  computed: {
    stableParent() {
      return STABLE_PARENT;
    },
    renderedTemplate() {
      const template = this.field?.template || "";
      const context = this.context;
      let interpolated = template;

      if (context) {
        interpolated = template.replace(/\{\{(.+?)\}\}/g, (match, expr) => {
          try {
            const result = Expressions.evaluateExpression(expr.trim(), context);
            return escapeHtml(result ?? "");
          } catch {
            return "";
          }
        });
      }

      return sanitize(interpolated);
    },
  },
  methods: {
    getNewDefaultEntry() {
      return "";
    },
  },
};
</script>
<style lang="scss" scoped></style>
